import {
  PRICE_CHANGE_NOTE_MAX,
  PRICE_CHANGE_REASONS,
  PRICE_CHANGE_REASON_LABELS,
  priceChangeReasonLabel,
  type PriceChangeReason,
} from '@barso/shared';
import { useState } from 'react';
import { Keypad } from '../components/Keypad';
import { ReasonChips } from '../components/ReasonChips';
import { Button, TextField } from '../components/ui';
import { euro } from '../format';
import { pushDigits, removeLastDigit } from '../pos/keypad';
import { validatePriceChange } from '../pos/priceChange';
import type { CartLine } from '../pos/types';
import { Sheet } from './posParts';

const OPTIONS = PRICE_CHANGE_REASONS.map((reason) => ({
  value: reason,
  label: PRICE_CHANGE_REASON_LABELS[reason],
}));

/**
 * Blatt „Preis korrigieren“ (U17g): neuer Preis über den Ziffernblock (ganze Cent, 0,00 bis 5.000 €), Grund als Pflicht (Chips),
 * ein Text bis 200 Zeichen (bei „Anderer Grund“ Pflicht). Der Preis gilt erst, wenn ein Grund gewählt ist; mit „Abbrechen“
 * bleibt der alte Preis. Jede Kassenkraft darf korrigieren (Entscheidung des Auftraggebers), alles ist protokolliert.
 */
export function PriceChangeSheet({
  line,
  onApply,
  onClose,
}: {
  line: CartLine;
  onApply: (cents: number, reason: PriceChangeReason, note: string | undefined) => void;
  onClose: () => void;
}) {
  // Ganze Cent, nie Text oder Gleitkomma (K6); 0 = noch nichts eingegeben
  const [cents, setCents] = useState(0);
  const [touched, setTouched] = useState(false);
  const [reason, setReason] = useState<PriceChangeReason | null>(null);
  const [note, setNote] = useState('');
  const text = note.trim();
  // 0,00 € ist als Preis erlaubt: erst „eingegeben“ ersetzt die Anzeige des alten Preises
  const problem = validatePriceChange(line, cents, reason, text === '' ? undefined : text);
  const hint = !touched
    ? 'Bitte den neuen Preis eintippen.'
    : problem === 'unchanged'
      ? 'Der neue Preis ist gleich dem alten.'
      : problem === 'reason'
        ? 'Bitte einen Grund wählen.'
        : problem === 'note'
          ? 'Bei „Anderer Grund“ bitte kurz beschreiben.'
          : null;
  const ready = touched && problem === null;
  const current = line.priceChange?.originalCents;
  return (
    <Sheet title="Preis korrigieren" dismissible={false} onClose={onClose}>
      <strong>{line.title}</strong>
      <span>
        {`Jetzt: ${euro(line.priceCents)}`}
        {current === undefined
          ? ''
          : ` (ursprünglich ${euro(current)}, ${priceChangeReasonLabel(line.priceChange?.reason)})`}
      </span>
      <p className="cash__given" aria-live="polite">
        Neuer Preis: <strong>{touched ? euro(cents) : '–'}</strong>
      </p>
      <Keypad
        onDigits={(digits) => {
          setTouched(true);
          setCents((value) => pushDigits(value, digits));
        }}
        onBackspace={() => {
          setCents(removeLastDigit);
        }}
      />
      <ReasonChips label="Grund (Pflicht)" options={OPTIONS} value={reason} onChange={setReason} />
      <TextField
        label={reason === 'other' ? 'Anmerkung (Pflicht)' : 'Anmerkung (freiwillig)'}
        hint="Bitte keine Namen oder Kontaktdaten."
        value={note}
        maxLength={PRICE_CHANGE_NOTE_MAX}
        autoComplete="off"
        onChange={(event) => {
          setNote(event.target.value);
        }}
      />
      <span className="reason__counter" aria-hidden="true">
        {`${String(Array.from(note).length)}/${String(PRICE_CHANGE_NOTE_MAX)}`}
      </span>
      {hint !== null && (
        <p className="muted" role="status">
          {hint}
        </p>
      )}
      <div className="row">
        <Button
          disabled={!ready || reason === null}
          onClick={() => {
            if (reason === null || !ready) return;
            onApply(cents, reason, text === '' ? undefined : text);
          }}
        >
          Preis übernehmen
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Abbrechen
        </Button>
      </div>
    </Sheet>
  );
}
